/**
 * Die Glyphen, die diese Anwendung braucht.
 *
 * Die Komponente `Icon` des Design-Systems löst Namen über `window.lucide` auf —
 * im Prototypen des Skills kommt das von unpkg. Hier stellen wir dieselbe Fläche
 * aus dem npm-Paket `lucide` her, mit benannten Importen: Ein Import des ganzen
 * Pakets brächte über zweitausend Symbole ins Bündel.
 *
 * Wer einen weiteren Glyphen braucht, trägt ihn hier ein. Der Name im JSX ist der
 * Lucide-Name mit Bindestrichen (`chevrons-up-down`), der Import der in
 * Großbuchstaben gefügte (`ChevronsUpDown`).
 *
 * **Die Fläche hat zwei Hälften, seit die Komponente aus `components/` kommt**
 * (#144): den Bestand `icons` und den Zeichner `createIcons`. Der abgelöste
 * Baustein löste die Symboldaten selbst auf und brauchte nur den Bestand; der des
 * Skills hängt ein `<i data-lucide="…">` ein und ruft `window.lucide.createIcons()`
 * **ohne Argumente** — so, wie die UMD-Datei sich bedienen lässt. Das npm-Paket
 * verlangt seinen Bestand dagegen ausdrücklich und wirft ohne ihn; deshalb steht
 * hier eine Hülle, die ihn einsetzt, und nicht der nackte Export.
 *
 * Nachgebaut ist daran nichts: Die Umschrift `chevron-down` → `ChevronDown` und
 * das Ersetzen des Platzhalters macht Lucide selbst. Fehlte diese Hälfte, bliebe
 * jede Glyphe der Anwendung leer — und weder Typprüfung noch Bau noch der
 * Rauchtest sähen es, denn ein leeres `<span>` ist ein gezeichnetes Element.
 * Dafür steht `icons.test.tsx`.
 */

import type { IconNode } from "lucide";
import {
  // Der Zeichner kommt aus demselben Paket wie die Glyphen und steht deshalb in
  // derselben Einfuhrzeile — vor der alphabetischen Liste, weil er keine ist.
  createIcons,
  ArrowLeft,
  Bot,
  Calendar,
  Check,
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  ChevronsLeft,
  ChevronsRight,
  ChevronsUpDown,
  CircleAlert,
  ClipboardCopy,
  Columns2,
  CornerDownRight,
  Cpu,
  Database,
  Eye,
  EyeOff,
  FileCode,
  Fingerprint,
  Flag,
  FolderGit2,
  Gauge,
  History,
  IdCard,
  Layers,
  ListChecks,
  ListPlus,
  Loader2,
  Lock,
  LockOpen,
  LogOut,
  MessageSquare,
  Minus,
  MoreVertical,
  Network,
  PanelLeft,
  Pause,
  Pencil,
  Play,
  Plus,
  RefreshCw,
  ScanSearch,
  Search,
  Send,
  Settings,
  Star,
  Sun,
  Target,
  Ticket,
  Timer,
  Trash2,
  TrendingUp,
  TriangleAlert,
  Undo2,
  Unplug,
  User,
  Users,
  Wrench,
  X,
} from "lucide";

/** Die Optionen, die der Baustein durchreicht — heute keine; er ruft argumentlos. */
type DrawOptions = Parameters<typeof createIcons>[0];

declare global {
  interface Window {
    lucide?: { icons: Record<string, IconNode>; createIcons: (options?: DrawOptions) => void };
  }
}

const icons: Record<string, IconNode> = {
  ArrowLeft,
  Bot,
  Calendar,
  Check,
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  ChevronsLeft,
  ChevronsRight,
  ChevronsUpDown,
  CircleAlert,
  ClipboardCopy,
  Columns2,
  CornerDownRight,
  Cpu,
  Database,
  Eye,
  EyeOff,
  FileCode,
  Fingerprint,
  Flag,
  FolderGit2,
  Gauge,
  History,
  IdCard,
  Layers,
  ListChecks,
  ListPlus,
  Loader2,
  Lock,
  LockOpen,
  LogOut,
  MessageSquare,
  Minus,
  MoreVertical,
  Network,
  PanelLeft,
  Pause,
  Pencil,
  Play,
  Plus,
  RefreshCw,
  ScanSearch,
  Search,
  Send,
  Settings,
  Star,
  Sun,
  Target,
  Ticket,
  Timer,
  Trash2,
  TrendingUp,
  TriangleAlert,
  Undo2,
  Unplug,
  User,
  Users,
  Wrench,
  X,
};

const inventory = { ...(window.lucide?.icons ?? {}), ...icons };

window.lucide = {
  icons: inventory,
  // Der Bestand steht **vor** den durchgereichten Optionen, damit ein Aufrufer
  // ihn überschreiben kann statt ihn zu verlieren — heute tut es keiner.
  createIcons: (options?: DrawOptions): void => createIcons({ icons: inventory, ...options }),
};

export {};
